/**
 * Operator-platform API client — readiness, fleets, corrective actions,
 * readiness packs. Mirrors the `vettingApi` pattern (fresh Clerk token per
 * request via the auth bridge).
 */
import type { ServerReadiness } from '../vessel/readiness';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8787';

async function fetchJson<T>(path: string, options?: RequestInit): Promise<T> {
  const { getFreshToken } = await import('../auth-bridge');
  const token = await getFreshToken();
  const doFetch = () =>
    fetch(`${API_URL}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...options?.headers,
      },
    });

  // One transparent retry on NETWORK failure ("Failed to fetch") — a worker
  // isolate restart or flaky connection shouldn't surface as a broken page.
  // Only for reads: retrying a POST/PATCH could double-apply a write.
  let res: Response;
  try {
    res = await doFetch();
  } catch (err) {
    const method = (options?.method ?? 'GET').toUpperCase();
    if (method !== 'GET') throw err;
    await new Promise((r) => setTimeout(r, 600));
    res = await doFetch();
  }

  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(`${res.status}: ${text}`);
  }
  return res.json() as Promise<T>;
}

// ── Fleet types ───────────────────────────────────────────────────────────────

export interface Fleet {
  id: string;
  name: string;
  vessel_count: number;
}

export interface FleetVesselRow {
  id: string;
  name: string | null;
  imo: string | null;
  vessel_type: string | null;
  flag_iso2: string | null;
  readiness_score: number | null;
  readiness_band: import('../vessel/readiness').ReadinessBand | null;
  risk_band: string | null;
  class_society: string | null;
  class_status: string | null;
  class_expiry: string | null;
  class_expiring_90d: boolean;
  open_actions: number;
  overdue_actions: number;
  psc_detentions_24m: number;
  last_psc_date: string | null;
  active_sanctions: number;
  last_inspection_id: string | null;
  last_inspection_date: string | null;
  last_self_inspection_date?: string | null;
  /** ISM §12 clock — no completed self-inspection in the last 12 months. */
  self_inspection_overdue?: boolean;
}

export interface FleetAttentionItem {
  vessel_id: string;
  vessel_name: string | null;
  kind: 'sanction' | 'overdue_action' | 'class_expiring' | 'detention' | 'self_inspection_due';
  severity: 'high' | 'medium';
  description: string;
  link: string;
}

export interface FleetDashboard {
  fleet: { id: string; name: string };
  stats: {
    vessels: number;
    avg_readiness: number | null;
    high_risk: number;
    open_actions: number;
    overdue_actions: number;
    class_expiring_90d: number;
    /** Owner KPIs (research-driven — see docs/internal-users-plan.md). */
    avg_observations_per_inspection_12m?: number | null;
    inspections_12m?: number;
    self_inspection_overdue?: number;
    tmsa_last_assessed_at?: string | null;
    tmsa_reassessment_due?: boolean;
  };
  vessels: FleetVesselRow[];
  attention: FleetAttentionItem[];
}

export interface SuggestedVessel {
  id: string;
  name: string | null;
  imo: string | null;
  mmsi: string | null;
  vessel_type: string | null;
  flag_iso2: string | null;
  registered_owner: string | null;
  ship_manager: string | null;
  operator: string | null;
}

// ── Corrective actions ────────────────────────────────────────────────────────

export type ActionStatus = 'open' | 'in_progress' | 'verified' | 'closed';
export type ActionSeverity = 'low' | 'medium' | 'high' | 'critical';

export interface CorrectiveAction {
  id: string;
  inspection_id: string | null;
  response_id: string | null;
  vessel_id: string | null;
  vessel_name?: string | null;
  question_ref: string | null;
  title: string;
  detail: string | null;
  severity: ActionSeverity;
  status: ActionStatus;
  owner_clerk_id: string | null;
  due_date: string | null;
  root_cause: string | null;
  closure_notes: string | null;
  closed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ActionDetail extends CorrectiveAction {
  response: {
    question_ref: string;
    answer: string | null;
    inspector_remark: string | null;
    ship_mgr_comment: string | null;
    ship_mgr_action: string | null;
  } | null;
  evidence: Array<{ id: string; kind: string; filename: string | null; created_at: string }>;
  vessel: { id: string; name: string | null; imo: string | null } | null;
}

export interface ActionFilters {
  vessel_id?: string;
  fleet_id?: string;
  status?: ActionStatus;
  overdue?: boolean;
}

// ── TMSA 3 self-assessment (fleet-scoped) ────────────────────────────────────

export interface TmsaElement {
  code: string;
  title: string;
  sub_elements: string[];
  summary: string;
  /** 0 = not yet assessed; 1–4 = attained stage. */
  stage: number;
  target_stage: number | null;
  notes: string | null;
  updated_at: string | null;
}

export interface TmsaAssessment {
  elements: TmsaElement[];
  assessed_count: number;
  element_count: number;
  avg_stage: number | null;
  /** True until supabase/migrations/013_tmsa_assessments.sql is applied. */
  migration_pending?: boolean;
}

export interface TmsaSaveItem {
  element_code: string;
  stage: number;
  target_stage?: number | null;
  notes?: string | null;
}

export const operatorApi = {
  /** Canonical server-side Vessel Readiness Score (computes + persists). */
  async getReadiness(vesselId: string): Promise<ServerReadiness> {
    const r = await fetchJson<{ data: ServerReadiness }>(`/api/v2/vessels/${vesselId}/readiness`);
    return r.data;
  },

  // ── Fleets ────────────────────────────────────────────────────────────────
  async listFleets(): Promise<Fleet[]> {
    const r = await fetchJson<{ data: Fleet[] }>('/api/v2/fleets');
    return r.data ?? [];
  },

  async createFleet(name: string): Promise<Fleet> {
    const r = await fetchJson<{ data: Fleet }>('/api/v2/fleets', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
    return r.data;
  },

  async getFleetDashboard(fleetId: string): Promise<FleetDashboard> {
    const r = await fetchJson<{ data: FleetDashboard }>(`/api/v2/fleets/${fleetId}/dashboard`);
    return r.data;
  },

  async addFleetVessels(fleetId: string, vesselIds: string[]): Promise<{ added: number }> {
    const r = await fetchJson<{ data: { added: number } }>(`/api/v2/fleets/${fleetId}/vessels`, {
      method: 'POST',
      body: JSON.stringify({ vessel_ids: vesselIds }),
    });
    return r.data;
  },

  async removeFleetVessel(fleetId: string, vesselId: string): Promise<void> {
    await fetchJson(`/api/v2/fleets/${fleetId}/vessels/${vesselId}`, { method: 'DELETE' });
  },

  // ── TMSA 3 self-assessment ────────────────────────────────────────────────
  async getTmsa(fleetId: string): Promise<TmsaAssessment> {
    const r = await fetchJson<{ data: TmsaAssessment }>(`/api/v2/fleets/${fleetId}/tmsa`);
    return r.data;
  },

  async saveTmsa(fleetId: string, items: TmsaSaveItem[]): Promise<TmsaAssessment> {
    const r = await fetchJson<{ data: TmsaAssessment }>(`/api/v2/fleets/${fleetId}/tmsa`, {
      method: 'PUT',
      body: JSON.stringify({ items }),
    });
    return r.data;
  },

  /** Ownership-derived bulk-add suggestions ("vessels managed by X"). */
  async suggestVessels(company: string): Promise<SuggestedVessel[]> {
    const r = await fetchJson<{ data: SuggestedVessel[] }>(
      `/api/v2/fleets/suggest-vessels?company=${encodeURIComponent(company)}`,
    );
    return r.data ?? [];
  },

  // ── Corrective actions ────────────────────────────────────────────────────
  async generateActions(inspectionId: string): Promise<{ created: number; total_observations: number }> {
    const r = await fetchJson<{ data: { created: number; total_observations: number } }>(
      `/api/v2/vetting/inspections/${inspectionId}/actions/generate`,
      { method: 'POST' },
    );
    return r.data;
  },

  async listActions(filters: ActionFilters = {}): Promise<CorrectiveAction[]> {
    const p = new URLSearchParams();
    if (filters.vessel_id) p.set('vessel_id', filters.vessel_id);
    if (filters.fleet_id) p.set('fleet_id', filters.fleet_id);
    if (filters.status) p.set('status', filters.status);
    if (filters.overdue) p.set('overdue', '1');
    const qs = p.toString() ? `?${p}` : '';
    const r = await fetchJson<{ data: CorrectiveAction[] }>(`/api/v2/actions${qs}`);
    return r.data ?? [];
  },

  async getAction(id: string): Promise<ActionDetail> {
    const r = await fetchJson<{ data: ActionDetail }>(`/api/v2/actions/${id}`);
    return r.data;
  },

  async updateAction(
    id: string,
    patch: Partial<Pick<CorrectiveAction, 'status' | 'owner_clerk_id' | 'due_date' | 'root_cause' | 'severity'>>,
  ): Promise<CorrectiveAction> {
    const r = await fetchJson<{ data: CorrectiveAction }>(`/api/v2/actions/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
    return r.data;
  },

  async closeAction(id: string, closureNotes: string): Promise<CorrectiveAction> {
    const r = await fetchJson<{ data: CorrectiveAction }>(`/api/v2/actions/${id}/close`, {
      method: 'POST',
      body: JSON.stringify({ closure_notes: closureNotes }),
    });
    return r.data;
  },

  // ── Readiness packs ───────────────────────────────────────────────────────
  async generateReadinessPack(vesselId: string): Promise<{
    pack_id: string;
    share_url: string;
    expires_at: string;
    format: 'pdf' | 'html';
    readiness_score: number;
    readiness_band: string;
  }> {
    const r = await fetchJson<{ data: { pack_id: string; share_url: string; expires_at: string; format: 'pdf' | 'html'; readiness_score: number; readiness_band: string } }>(
      `/api/v2/vessels/${vesselId}/readiness-pack`,
      { method: 'POST' },
    );
    return r.data;
  },
};
